import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Shield, UserPlus, Trash2, Building2, Crown, ArrowUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/** Manage sub-super-admins (visible only to root super_admin). */
export default function Admins() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", is_root: false, max_companies: 0 });

  const load = async () => {
    try { const { data } = await api.get("/admins"); setItems(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password.trim()) return toast.error("املأ كل الحقول");
    try {
      await api.post("/admins", form);
      toast.success(form.is_root ? "تم إضافة مدير رئيسي" : "تم إضافة مدير فرعي");
      setForm({ name: "", email: "", password: "", phone: "", is_root: false, max_companies: 0 }); setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleRole = async (a) => {
    const target = a.is_root ? "فرعي" : "رئيسي";
    if (!confirm(`تحويل ${a.name} إلى مدير عام ${target}؟`)) return;
    try { await api.post(`/admins/${a.id}/toggle-root`); toast.success(`تم التحويل إلى ${target}`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const setLimit = async (a) => {
    const current = a.max_companies || 0;
    const val = prompt(`أدخل عدد الشركات المسموح للمدير «${a.name}» (0 = غير محدود):`, String(current));
    if (val === null) return;
    const n = Math.max(0, parseInt(val) || 0);
    try { await api.post(`/admins/${a.id}/limit`, { max_companies: n }); toast.success(n === 0 ? "تم إلغاء الحد" : `الحد الآن ${n} شركات`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (id) => {
    if (!confirm("حذف المدير الفرعي؟ سيتم حذف كل شركاته وبياناته أيضاً — لا يمكن التراجع!")) return;
    try { const { data } = await api.delete(`/admins/${id}`); toast.success(`تم الحذف — ${data.cascaded_companies} شركة مرتبطة حُذفت`); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!user?.is_root) {
    return (
      <div className="border border-red-300 bg-red-50 rounded-md p-6 text-center" data-testid="admins-forbidden">
        <Shield className="w-8 h-8 text-red-600 mx-auto mb-2" />
        <div className="font-bold text-red-900">هذه الصفحة مخصصة للمدير الرئيسي فقط</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admins-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><Crown className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">المديرون الفرعيون</h1>
          <p className="text-sm text-muted-foreground">أنت المدير الرئيسي 👑 — يمكنك إضافة مديرين فرعيين، وكل واحد سيرى شركاته فقط. أنت وحدك ترى كل شيء ولا يستطيعون تعديل حسابك.</p>
        </div>
        <Button onClick={()=>setOpen(true)} className="gap-2" data-testid="new-admin-btn"><UserPlus className="w-4 h-4" /> مدير فرعي جديد</Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="text-xs text-amber-700 flex items-center gap-1"><Crown className="w-3 h-3" /> أنت</div>
          <div className="text-lg font-bold text-amber-900 mt-1">{user.name}</div>
          <div className="text-[10px] text-amber-700 mt-1">المدير الرئيسي — يرى كل شيء</div>
        </Card>
        <Card className="p-4 border-primary/20 bg-primary/5">
          <div className="text-xs text-primary">المديرون الفرعيون</div>
          <div className="text-2xl font-bold text-primary mt-1">{items.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">إجمالي شركاتهم</div>
          <div className="text-2xl font-bold mt-1">{items.reduce((s, x) => s + (x.companies_count || 0), 0)}</div>
        </Card>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>الاسم</TableHead><TableHead>الإيميل</TableHead>
            <TableHead>الهاتف</TableHead><TableHead>عدد شركاته</TableHead>
            <TableHead>تاريخ الإضافة</TableHead><TableHead>إجراءات</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map(a => (
              <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                <TableCell className="font-bold flex items-center gap-1">{a.name}
                  {a.is_root
                    ? <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">👑 مدير عام رئيسي</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded bg-primary/10 text-primary font-bold">مدير عام فرعي</span>}
                </TableCell>
                <TableCell className="text-xs font-mono" dir="ltr">{a.email}</TableCell>
                <TableCell className="text-xs font-mono" dir="ltr">{a.phone || "—"}</TableCell>
                <TableCell><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary/10 text-primary text-xs font-bold w-fit"><Building2 className="w-3 h-3" /> {a.companies_count || 0}{a.max_companies ? ` / ${a.max_companies}` : ""}</span></TableCell>
                <TableCell className="text-xs">{new Date(a.created_at).toLocaleDateString("ar-EG")}</TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {a.seeded ? (
                      <span className="text-[10px] px-2 py-1 rounded bg-amber-100 text-amber-800 font-bold border border-amber-300" title="المدير المؤسس محمي — لا يمكن تعديله">🔒 مؤسس محمي</span>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={()=>setLimit(a)} className="h-7 text-xs" data-testid={`limit-${a.id}`}>حد الشركات</Button>
                        <Button size="sm" variant="outline" onClick={()=>toggleRole(a)} className="h-7 text-xs gap-1" data-testid={`toggle-${a.id}`}><ArrowUpDown className="w-3 h-3" />{a.is_root ? "تحويل لفرعي" : "ترقية لرئيسي"}</Button>
                        <Button size="sm" variant="ghost" onClick={()=>del(a.id)} className="h-7 text-destructive" data-testid={`del-admin-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">لا يوجد مديرون فرعيون بعد</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader><DialogTitle>إضافة مدير فرعي</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>الاسم</Label><Input value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="admin-name-input" /></div>
            <div><Label>الإيميل</Label><Input value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} dir="ltr" className="font-mono" data-testid="admin-email-input" /></div>
            <div><Label>كلمة المرور</Label><Input type="password" value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})} data-testid="admin-pw-input" /></div>
            <div><Label>الهاتف</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} dir="ltr" className="font-mono" data-testid="admin-phone-input" /></div>
            <div><Label>الحد الأقصى لعدد الشركات (0 = غير محدود)</Label><Input type="number" min="0" value={form.max_companies} onChange={(e)=>setForm({...form, max_companies:+e.target.value || 0})} data-testid="admin-max-companies-input" /></div>
            <label className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer">
              <input type="checkbox" checked={form.is_root} onChange={(e)=>setForm({...form, is_root:e.target.checked})} data-testid="admin-is-root-cb" />
              <Crown className="w-4 h-4 text-amber-600" />
              <span className="font-bold">أنشئه كمدير عام رئيسي (يرى كل شيء) — بدلاً من فرعي</span>
            </label>
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              🔒 سيرى هذا المدير الفرعي فقط الشركات التي يُنشئها هو، ولن يستطيع رؤية أو تعديل شركاتك أنت.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-admin-btn">إضافة</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
